import { Plus, Trash2, UploadCloud, X } from 'lucide-react';
import React, { useRef, useState } from 'react';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { supabase } from '../../../../lib/supabase';
import { uploadBlogImage } from '../../BlogEditorPage';

export interface GalleryEditorData {
  images: { url: string; alt?: string; caption?: string }[];
  columns?: 1 | 2 | 3;
}

interface Props {
  data: GalleryEditorData;
  onChange: (data: Record<string, unknown>) => void;
}

const BLOG_PUBLISH_API_KEY = import.meta.env.VITE_BLOG_PUBLISH_API_KEY as string | undefined;

const ImageSlot: React.FC<{
  image: { url: string; alt?: string; caption?: string };
  index: number;
  onUpdate: (patch: Partial<{ url: string; alt: string; caption: string }>) => void;
  onRemove: () => void;
}> = ({ image, index, onUpdate, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!BLOG_PUBLISH_API_KEY) { setUploadError('API key not configured.'); return; }
    setUploading(true);
    setUploadError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? '';
      if (!token) throw new Error('Not authenticated');
      const url = await uploadBlogImage(file, BLOG_PUBLISH_API_KEY, token);
      onUpdate({ url });
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ds-card-flush">
      {/* Image preview / drop zone */}
      <div
        className="relative"
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
        onDragOver={(e) => e.preventDefault()}
      >
        {image.url ? (
          <div className="relative">
            <img
              src={image.url}
              alt={image.alt ?? `Image ${index + 1}`}
              className="w-full h-32 object-cover"
            />
            <button
              type="button"
              onClick={() => onUpdate({ url: '' })}
              className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
              title="Remove image"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 gap-2 border-b" style={{ borderColor: 'var(--ds-border)' }}>
            {uploading ? (
              <LoadingSpinner size="sm" showText={false} />
            ) : (
              <UploadCloud className="w-5 h-5" style={{ color: 'var(--ds-text-muted)' }} />
            )}
            <p className="t-meta">
              {uploading ? 'Uploading…' : `Image ${index + 1}`}
            </p>
            {!uploading && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                Choose
              </button>
            )}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </div>

      {/* Fields */}
      <div className="p-2 flex flex-col gap-1.5">
        {uploadError && <p className="t-meta t-danger">{uploadError}</p>}
        <input
          type="url"
          value={image.url}
          onChange={(e) => onUpdate({ url: e.target.value })}
          placeholder="Or paste image URL"
          className="ds-input ds-input-sm"
        />
        <input
          type="text"
          value={image.alt ?? ''}
          onChange={(e) => onUpdate({ alt: e.target.value })}
          placeholder="Alt text"
          className="ds-input ds-input-sm"
        />
        <input
          type="text"
          value={image.caption ?? ''}
          onChange={(e) => onUpdate({ caption: e.target.value })}
          placeholder="Caption (optional)"
          className="ds-input ds-input-sm"
        />
        <button
          type="button"
          onClick={onRemove}
          className="ds-btn ds-btn-ghost ds-btn-sm w-fit hover:text-[var(--ds-danger)]"
        >
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      </div>
    </div>
  );
};

export const GallerySectionEditor: React.FC<Props> = ({ data, onChange }) => {
  const images = data.images ?? [];
  const columns = data.columns ?? 2;

  const set = (patch: Partial<GalleryEditorData>) => onChange({ ...data, ...patch });

  const updateImage = (idx: number, patch: Partial<{ url: string; alt: string; caption: string }>) =>
    set({ images: images.map((img, i) => (i === idx ? { ...img, ...patch } : img)) });

  const addImage = () => set({ images: [...images, { url: '', alt: '', caption: '' }] });

  const removeImage = (idx: number) => set({ images: images.filter((_, i) => i !== idx) });

  return (
    <div className="flex flex-col gap-4">
      {/* Columns selector */}
      <div className="flex items-center gap-3">
        <span className="t-label shrink-0">Columns:</span>
        {([1, 2, 3] as const).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => set({ columns: n })}
            className={`ds-chip ds-chip-button ${
              columns === n
                ? 'is-active'
                : ''
            }`}
            aria-selected={columns === n ? 'true' : undefined}
          >
            {n}
          </button>
        ))}
      </div>

      {/* Image slots */}
      <div className={`grid gap-3 ${columns === 1 ? 'grid-cols-1' : columns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {images.map((img, idx) => (
          <ImageSlot
            key={idx}
            image={img}
            index={idx}
            onUpdate={(patch) => updateImage(idx, patch)}
            onRemove={() => removeImage(idx)}
          />
        ))}

        {/* Add slot */}
        <button
          type="button"
          onClick={addImage}
          className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed t-muted hover:text-[var(--ds-text-strong)] hover:border-[var(--ds-brand)] transition-colors gap-2"
          style={{ borderColor: 'var(--ds-border)' }}
        >
          <Plus className="w-5 h-5" />
          <span className="t-meta">Add image</span>
        </button>
      </div>
    </div>
  );
};
